import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  enUS,
  frFR,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fDate } from 'src/helpers/formatTime';
import { useAppDispatch } from 'src/hooks/store';
import {
  cancelOrganizationSubscriptionAsAdmin,
  deleteOrganization,
  updateOrganizationSubscription,
} from 'src/slices/organization';
import { User } from 'src/types/auth.type';
import { Organization } from 'src/types/organization.type';
import {
  Subscription,
  UpdateSubscriptionDto,
} from 'src/types/subscription.type';
import { ConfirmationDialog } from '../basic/ConfirmationDialog';
import { Flex } from '../basic/Flex';
import { CustomLoadingOverlay } from '../dataGrid/CustomLoadingOverlay';
import { CustomNoRowsOverlay } from '../dataGrid/CustomNoRowsOverlay';
import SearchFormField from '../forms/SearchFormField';
import OrganizationCard from './OrganizationCard';

type SubscriptionType =
  | 'manual'
  | 'automatic'
  | 'free'
  | 'freeTrialAutomatic'
  | 'freeTrialManual'
  | 'cancelled'
  | 'none';

type GridRowType = Organization & {
  id: string;
  subscriptionType: SubscriptionType;
} & Pick<Subscription, 'maxNumberOfUsers'>;
interface Props {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  setSelectedOrganization: (organization: Organization | null) => void;
  users: User[];
}
export const AdminOrganizations: React.FC<Props> = ({
  organizations,
  selectedOrganization,
  setSelectedOrganization,
  users,
}) => {
  const dispatch = useAppDispatch();

  const { t, i18n } = useTranslation();

  const [selectedOrganizationsIds, setSelectedOrganizationsIds] = useState<
    string[]
  >([]);
  const [clickedOrganizationId, setClickedOrganizationId] = useState<
    string | null
  >(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isDialogOpened, setDialogOpened] = useState<boolean>(false);

  const [deleteOrganizationDialog, setDeleteOrganizationDialog] =
    useState<string>('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [
    cancelSubscriptionDialogSubscriptionId,
    setCancelSubscriptionDialogSubscriptionId,
  ] = useState<{
    organizationId: string;
    subscriptionId: string;
  }>();

  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = useState<
    SubscriptionType | ''
  >('');

  const selectedOrganizations = useMemo(
    () =>
      selectedOrganizationsIds
        .map((id) =>
          organizations.find((organization) => organization._id === id),
        )
        .filter((organization): organization is Organization => !!organization),
    [selectedOrganizationsIds, organizations],
  );

  const clickedOrganization = useMemo(
    () =>
      organizations.find(
        (organization) => organization._id === clickedOrganizationId,
      ),
    [clickedOrganizationId, organizations],
  );

  useMemo(() => {
    if (selectedOrganization) {
      setDialogOpened(true);
      setClickedOrganizationId(selectedOrganization._id);
      setSearchQuery(selectedOrganization.name);
      setSelectedOrganization(null);
    }
  }, [selectedOrganization, setSelectedOrganization]);

  const getSubscriptionTypeString = (
    organization: Organization,
  ): SubscriptionType => {
    const sub = organization.subscription;
    if (sub?.isFreeSubscription) {
      return 'free';
    }
    if (sub?.isFreeTrial) {
      if (sub?.isAutomaticSubscriptionPaymentActive) {
        return 'freeTrialAutomatic';
      }
      return 'freeTrialManual';
    }
    if (sub?.isPaying) {
      return 'manual';
    }
    if (sub?.isAutomaticSubscriptionPaymentActive) {
      return 'automatic';
    }
    if (
      sub?.subscriptionPeriodStartDate ||
      sub?.subscriptionPeriodEndDate ||
      (sub?.subscriptionPeriods && sub?.subscriptionPeriods?.length > 0)
    ) {
      return 'cancelled';
    }
    return 'none';
  };

  const filteredOrganizations = useMemo(
    () =>
      organizations.filter((organization) => {
        const matchesSearchQuery = organization.name
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const matchesSubscriptionType =
          !subscriptionTypeFilter ||
          getSubscriptionTypeString(organization) === subscriptionTypeFilter;
        return matchesSearchQuery && matchesSubscriptionType;
      }),
    [searchQuery, subscriptionTypeFilter, organizations],
  );

  const getMaxColumnWidth = (field: keyof GridRowType, size: number) => {
    const maxLength = Math.max(
      ...filteredOrganizations.map((organization) => {
        return (organization as any)[field]?.toString().length || 0;
      }),
    );
    return maxLength * size;
  };

  const columns: GridColDef<GridRowType>[] = [
    {
      field: 'name',
      headerName: t(`organization.fields.name.columnHeader`),
      minWidth: getMaxColumnWidth('name', 10),
    },
    {
      field: 'type',
      headerName: t('organization.fields.type.columnHeader'),
      renderCell: (cellValues) =>
        cellValues.row.type
          ? t(`organization.fields.type.options.${cellValues.row.type}`)
          : '',
      minWidth: getMaxColumnWidth('type', 13),
    },
    {
      field: 'subscriptionType',
      headerName: t('pages.Admin.subscription.title'),
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValues) => {
        if (cellValues.row.subscriptionType === 'none') {
          return <CloseIcon color="error" style={{ marginLeft: 8 }} />;
        } else {
          return t(
            `pages.Admin.subscription.tableColumnHeaders.${cellValues.row.subscriptionType}`,
          );
        }
      },
      minWidth: 200,
    },
    {
      field: 'totalMembers',
      headerName: t('pages.Admin.organization.totalMembers'),
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValues) => (
        <Typography>{cellValues.row.members?.length}</Typography>
      ),
      minWidth: 120,
    },
    {
      field: 'maxNumberOfUsers',
      headerName: t('pages.Admin.organization.maxNumberOfUsers'),
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValues) => (
        <Typography>{cellValues.row.maxNumberOfUsers ?? 0}</Typography>
      ),
    },
    {
      field: 'totalVictimes',
      headerName: t('pages.Admin.totalVictimes'),
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValues) => (
        <Typography>{cellValues.row.victimes?.length || 0}</Typography>
      ),
    },
    {
      field: 'subscriptionPeriodStartDate',
      headerName: t(
        'pages.Admin.organization.columnHeader.subscriptionPeriodStartDate',
      ),
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValues) => (
        <Typography>
          {cellValues.row.subscription?.subscriptionPeriodStartDate
            ? fDate(cellValues.row.subscription?.subscriptionPeriodStartDate)
            : '-'}
        </Typography>
      ),
      minWidth: 210,
    },
    {
      field: 'subscriptionPeriodEndDate',
      headerName: t(
        'pages.Admin.organization.columnHeader.subscriptionPeriodEndDate',
      ),
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValues) => (
        <Typography>
          {cellValues.row.subscription?.subscriptionPeriodEndDate
            ? fDate(cellValues.row.subscription?.subscriptionPeriodEndDate)
            : '-'}
        </Typography>
      ),
      minWidth: 200,
    },
    {
      field: 'creationDate',
      headerName: t('pages.Admin.organization.creationDate'),
      headerAlign: 'center',
      align: 'center',
      minWidth: 140,
      valueGetter: (params) => new Date(params.row.createdAt),
      renderCell: (cellValues) => (
        <Typography>
          {new Date(cellValues.row.createdAt).toLocaleDateString()}
        </Typography>
      ),
    },
  ];

  const rows: GridRowType[] = filteredOrganizations.map((organization) => ({
    ...organization,
    id: organization._id,
    totalMembers: organization.members?.length,
    totalVictimes: organization.victimes?.length || 0,
    subscriptionType: getSubscriptionTypeString(organization),
    maxNumberOfUsers: (organization.subscription?.maxNumberOfUsers || 0) + 1,
  }));

  const dialogAdminDeleteAccount = async () => {
    if (deleteOrganizationDialog !== '') {
      await dispatch(
        deleteOrganization({ organizationId: deleteOrganizationDialog }),
      );
      setOpenDeleteDialog(false);
      setDeleteOrganizationDialog('');
    }
  };

  const updateSubscription = async (
    organizationId: string,
    updateSubscriptionDto: UpdateSubscriptionDto,
  ) => {
    dispatch(
      updateOrganizationSubscription({
        organizationId,
        dto: updateSubscriptionDto,
      }),
    );
  };

  const cancelSubscription = async () => {
    if (cancelSubscriptionDialogSubscriptionId) {
      dispatch(
        cancelOrganizationSubscriptionAsAdmin(
          cancelSubscriptionDialogSubscriptionId.organizationId,
        ),
      );
    }
    setCancelSubscriptionDialogSubscriptionId(undefined);
  };

  const onClickRow = (params: GridRowParams<GridRowType>) => {
    setDialogOpened(true);
    setClickedOrganizationId(params.row._id);
  };

  const displayOrganizationCard = (organization: Organization) => (
    <OrganizationCard
      organization={organization}
      updateSubscription={(dto) => updateSubscription(organization._id, dto)}
      cancelSubscription={() =>
        cancelOrganizationSubscriptionAsAdmin(organization._id)
      }
      users={users}
      dispatch={dispatch}
    />
  );
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        style={{ marginBottom: 10, marginTop: 10 }}
      >
        <SearchFormField
          placeholderKey={t('pages.Admin.organization.search')}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sx={{ width: 400 }}
        />
        <FormControl
          sx={{
            width: 300,
            padding: 1,
            borderRadius: 1,
          }}
        >
          <InputLabel shrink>
            {t('pages.Admin.subscription.filterLabel')}
          </InputLabel>
          <Select
            value={subscriptionTypeFilter}
            onChange={(e) => {
              const value = e.target.value as SubscriptionType | '';
              setSubscriptionTypeFilter(value);
            }}
            displayEmpty
          >
            <MenuItem value="">
              <em>{t('pages.Admin.subscription.tableColumnHeaders.all')}</em>
            </MenuItem>
            <MenuItem value="manual">
              {t('pages.Admin.subscription.tableColumnHeaders.manual')}
            </MenuItem>
            <MenuItem value="automatic">
              {t('pages.Admin.subscription.tableColumnHeaders.automatic')}
            </MenuItem>
            <MenuItem value="free">
              {t('pages.Admin.subscription.tableColumnHeaders.free')}
            </MenuItem>
            <MenuItem value="freeTrialAutomatic">
              {t(
                'pages.Admin.subscription.tableColumnHeaders.freeTrialAutomatic',
              )}
            </MenuItem>
            <MenuItem value="freeTrialManual">
              {t('pages.Admin.subscription.tableColumnHeaders.freeTrialManual')}
            </MenuItem>
            <MenuItem value="cancelled">
              {t('pages.Admin.subscription.tableColumnHeaders.cancelled')}
            </MenuItem>
            <MenuItem value="none">
              {t('pages.Admin.subscription.tableColumnHeaders.none')}
            </MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <DataGrid
        sx={{
          cursor: 'default',
          boxShadow:
            'rgba(145, 158, 171, 0.24) 0px 0px 2px 0px, rgba(145, 158, 171, 0.24) 0px 16px 32px -4px',
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus, \
           &.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within':
            {
              outline: 'none',
            },
        }}
        localeText={
          i18n.language === 'en'
            ? enUS.components.MuiDataGrid.defaultProps.localeText
            : frFR.components.MuiDataGrid.defaultProps.localeText
        }
        components={{
          LoadingOverlay: CustomLoadingOverlay,
          NoRowsOverlay: () => (
            <CustomNoRowsOverlay
              text={t('pages.Admin.organization.noOrganizations')}
            />
          ),
          NoResultsOverlay: CustomNoRowsOverlay,
        }}
        loading={false}
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        onRowClick={onClickRow}
        onRowSelectionModelChange={(gridOrganizations) =>
          setSelectedOrganizationsIds(
            gridOrganizations.map((gridOrganization) =>
              gridOrganization.toString(),
            ),
          )
        }
        pageSizeOptions={[10, 20, 50]}
      />
      <Flex
        sx={{
          marginTop: 3,
          marginBottom: 1,
          justifyContent: 'flex-end',
          marginX: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            setDialogOpened(true);
          }}
          disabled={selectedOrganizations?.length === 0}
        >
          {t('pages.Admin.organization.showOrganizations')}
        </Button>
      </Flex>
      <Dialog
        open={Boolean(
          isDialogOpened &&
            (selectedOrganizations?.length !== 0 || clickedOrganization),
        )}
        onClose={() => {
          setDialogOpened(false);
          setSelectedOrganizationsIds([]);
          setClickedOrganizationId(null);
        }}
      >
        {clickedOrganization ? (
          displayOrganizationCard(clickedOrganization)
        ) : selectedOrganizations?.length === 1 &&
          selectedOrganizations?.[0] ? (
          displayOrganizationCard(selectedOrganizations[0])
        ) : selectedOrganizations?.length > 1 ? (
          <DialogContent>
            <Grid container spacing={2}>
              {selectedOrganizations.map((organization) => {
                return (
                  <Grid item xs={12} key={organization._id}>
                    {displayOrganizationCard(organization)}
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
        ) : null}
      </Dialog>
      <ConfirmationDialog
        title={t('pages.Admin.organization.confirmDeleteOrganizationTitle')}
        open={openDeleteDialog}
        onConfirm={dialogAdminDeleteAccount}
        onCancel={() => {
          setOpenDeleteDialog(false);
          setDeleteOrganizationDialog('');
        }}
      />
      <ConfirmationDialog
        title={t('pages.Admin.confirmCancelSubscription.title')}
        open={!!cancelSubscriptionDialogSubscriptionId}
        description={t('pages.Admin.confirmCancelSubscription.description')}
        onConfirm={cancelSubscription}
        onCancel={() => setCancelSubscriptionDialogSubscriptionId(undefined)}
      />
    </Box>
  );
};
